import { z } from 'zod';
import { 
  BaseDocument, 
  SoftDelete, 
  UserStatus, 
  PopulatedReference,
  ObjectIdSchema,
  BaseDocumentSchema,
  SoftDeleteSchema,
  UserStatusSchema,
} from '@/types/common';

// Updated interface with additional fields
export interface IUser extends BaseDocument, SoftDelete {
  // Authentication fields
  email: string;
  password: string;
  userType: 'student' | 'school_admin' | 'super_admin';
  status: UserStatus;
  emailVerified: boolean;
  emailVerificationToken?: string;
  passwordResetToken?: string;
  passwordResetExpires?: Date;
  lastLogin?: Date;
  loginAttempts: number;
  lockUntil?: Date;
  
  // Profile references
  studentProfile?: PopulatedReference<any>;
  schoolProfile?: PopulatedReference<any>;
  adminProfile?: PopulatedReference<any>;
  
  // Security
  twoFactorSecret?: string;
  twoFactorEnabled: boolean;
  
  // Extended profile fields
  name: string;
  avatarUrl?: string;
  grade: string;
  role: "student" | "school" | "super_admin";
  joinDate: Date;
  totalPoints: number;
  tasksCompleted: number;
  streakDays: number;
  level: number;
  rank: number;
  
  // NEW: Additional personal info fields
  phoneNumber?: string;
  dateOfBirth?: Date;
  address?: string;
  parentName?: string;
  parentEmail?: string;
  parentPhone?: string;
  schoolName?: string;
  
  // Preferences
  preferences: {
    notifications: boolean;
    soundEffects: boolean;
    theme: "light" | "dark";
    language: string;
    timezone: string;
    notificationSettings: {
      email: boolean;
      push: boolean;
      sms: boolean;
    };
  };
  
  // Metadata
  metadata: {
    registrationSource: string;
    ipAddress?: string;
    userAgent?: string;
    referrer?: string;
  };
}

// Date transformer for Zod
const dateSchema = z.union([z.date(), z.string().datetime()]).transform((val) => {
  if (typeof val === 'string') {
    return new Date(val);
  }
  return val;
});

// String date for form inputs (YYYY-MM-DD)
const dateStringSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format');

// Updated validation schema with additional fields
export const UserSchema = BaseDocumentSchema.merge(SoftDeleteSchema).extend({
  email: z.string().email('Invalid email format'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  userType: z.enum(['student', 'school_admin', 'super_admin']).default('student'),
  status: UserStatusSchema.default('active'),
  emailVerified: z.boolean().default(false),
  emailVerificationToken: z.string().optional(),
  passwordResetToken: z.string().optional(),
  passwordResetExpires: dateSchema.optional(),
  lastLogin: dateSchema.optional(),
  loginAttempts: z.number().default(0),
  lockUntil: dateSchema.optional(),
  
  // Profile references
  studentProfile: z.union([ObjectIdSchema, z.any()]).optional(),
  schoolProfile: z.union([ObjectIdSchema, z.any()]).optional(),
  adminProfile: z.union([ObjectIdSchema, z.any()]).optional(),
  
  // Security
  twoFactorSecret: z.string().optional(),
  twoFactorEnabled: z.boolean().default(false),
  
  // Extended profile
  name: z.string().min(1, 'Name is required'),
  avatarUrl: z.string().url().optional().or(z.literal('')),
  grade: z.string().default('Grade 10'),
  role: z.enum(['student', 'school', 'super_admin']).default('student'),
  joinDate: dateSchema.default(() => new Date()),
  totalPoints: z.number().min(0).default(0),
  tasksCompleted: z.number().min(0).default(0),
  streakDays: z.number().min(0).default(0),
  level: z.number().min(1).default(1),
  rank: z.number().min(0).default(0),
  
  // NEW: Additional personal info fields
  phoneNumber: z.string().optional(),
  dateOfBirth: dateSchema.optional(),
  address: z.string().optional(),
  parentName: z.string().optional(),
  parentEmail: z.string().email('Invalid parent email format').optional().or(z.literal('')),
  parentPhone: z.string().optional(),
  schoolName: z.string().optional(),
  
  // Combined preferences
  preferences: z.object({
    notifications: z.boolean().default(true),
    soundEffects: z.boolean().default(false),
    theme: z.enum(['light', 'dark']).default('light'),
    language: z.string().default('en'),
    timezone: z.string().default('Africa/Lagos'),
    notificationSettings: z.object({
      email: z.boolean().default(true),
      push: z.boolean().default(true),
      sms: z.boolean().default(false),
    }).default({}),
  }).default({}),
  
  metadata: z.object({
    registrationSource: z.string().default('web'),
    ipAddress: z.string().optional(),
    userAgent: z.string().optional(),
    referrer: z.string().optional(),
  }).default({}),
});

// Create user input (for registration) - UPDATED with additional fields
export const CreateUserSchema = z.object({
  email: z.string().email('Invalid email format'),
  password: z.string().min(8, 'Password must be at least 8 characters').max(128),
  confirmPassword: z.string(),
  name: z.string().min(1, 'Name is required').default(''),
  grade: z.string().optional().default('Grade 10'),
  role: z.enum(['student', 'school', 'super_admin']).optional().default('student'),
  avatarUrl: z.string().optional().default(''),
  
  // NEW: Additional personal info fields for signup
  phoneNumber: z.string().optional(),
  dateOfBirth: dateStringSchema.optional(),
  address: z.string().optional(),
  parentName: z.string().optional(),
  parentEmail: z.string().email('Invalid parent email format').optional().or(z.literal('')),
  parentPhone: z.string().optional(),
  schoolName: z.string().optional(),
  
  preferences: z.object({
    notifications: z.boolean().optional().default(true),
    soundEffects: z.boolean().optional().default(false),
    theme: z.enum(['light', 'dark']).optional().default('light'),
    language: z.string().optional().default('en'),
    timezone: z.string().optional().default('Africa/Lagos'),
    notificationSettings: z.object({
      email: z.boolean().optional().default(true),
      push: z.boolean().optional().default(true),
      sms: z.boolean().optional().default(false),
    }).optional().default({}),
  }).optional().default({}),
  metadata: z.object({
    registrationSource: z.string().optional().default('web'),
    ipAddress: z.string().optional(),
    userAgent: z.string().optional(),
    referrer: z.string().optional(),
  }).optional().default({}),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

// Update user input - UPDATED
export const UpdateUserSchema = UserSchema.partial().omit({
  _id: true,
  createdAt: true,
  updatedAt: true,
  password: true, // Password updates should use separate endpoint
});

// Login input
export const LoginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional().default(false),
});

// Password reset input
export const PasswordResetSchema = z.object({
  token: z.string(),
  newPassword: z.string().min(8).max(128),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

// Change password input
export const ChangePasswordSchema = z.object({
  currentPassword: z.string(),
  newPassword: z.string().min(8).max(128),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

// User response (excludes sensitive fields) - UPDATED with additional fields
export const UserResponseSchema = z.object({
  _id: z.union([ObjectIdSchema, z.string()]),
  email: z.string().email(),
  userType: z.enum(['student', 'school_admin', 'super_admin']).default('student'),
  status: UserStatusSchema.default('active'),
  emailVerified: z.boolean().default(false),
  lastLogin: dateSchema.optional(),
  loginAttempts: z.number().default(0),
  lockUntil: dateSchema.optional(),
  
  // Profile references
  studentProfile: z.union([ObjectIdSchema, z.any()]).optional(),
  schoolProfile: z.union([ObjectIdSchema, z.any()]).optional(),
  adminProfile: z.union([ObjectIdSchema, z.any()]).optional(),
  
  // Security
  twoFactorEnabled: z.boolean().default(false),
  
  // Extended profile
  name: z.string(),
  avatarUrl: z.string().optional(),
  grade: z.string().default('Grade 10'),
  role: z.enum(['student', 'school', 'super_admin']).default('student'),
  joinDate: dateSchema.default(() => new Date()),
  totalPoints: z.number().default(0),
  tasksCompleted: z.number().default(0),
  streakDays: z.number().default(0),
  level: z.number().default(1),
  rank: z.number().default(0),
  
  // NEW: Additional personal info fields
  phoneNumber: z.string().optional(),
  dateOfBirth: dateSchema.optional(),
  address: z.string().optional(),
  parentName: z.string().optional(),
  parentEmail: z.string().optional(),
  parentPhone: z.string().optional(),
  schoolName: z.string().optional(),
  
  // Combined preferences
  preferences: z.object({
    notifications: z.boolean().default(true),
    soundEffects: z.boolean().default(false),
    theme: z.enum(['light', 'dark']).default('light'),
    language: z.string().default('en'),
    timezone: z.string().default('Africa/Lagos'),
    notificationSettings: z.object({
      email: z.boolean().default(true),
      push: z.boolean().default(true),
      sms: z.boolean().default(false),
    }).default({}),
  }).default({}),
  
  metadata: z.object({
    registrationSource: z.string().default('web'),
    ipAddress: z.string().optional(),
    userAgent: z.string().optional(),
    referrer: z.string().optional(),
  }).default({}),
  
  // Base document fields
  createdAt: dateSchema.default(() => new Date()),
  updatedAt: dateSchema.default(() => new Date()),
  isDeleted: z.boolean().default(false),

  // Auth tokens (present after login/register)
  accessToken: z.string().optional(),
  refreshToken: z.string().optional(),

  // Student routing flags (returned by backend login so frontend can route without extra request)
  onboardingCompleted: z.boolean().optional(),
  hasTraitProfile: z.boolean().optional(),
});

// Types
export type CreateUserInput = z.infer<typeof CreateUserSchema>;
export type UpdateUserInput = z.infer<typeof UpdateUserSchema>;
export type LoginInput = z.infer<typeof LoginSchema>;
export type PasswordResetInput = z.infer<typeof PasswordResetSchema>;
export type ChangePasswordInput = z.infer<typeof ChangePasswordSchema>;
export type UserResponse = z.infer<typeof UserResponseSchema>;

// Collection name (for reference)
export const USER_COLLECTION = 'users';